<template>
    <v-form v-model="isValid">
        <v-row no-gutters>
            <v-col cols="11" class="title px-3 pt-2"> Nom de l'indicateur </v-col>
            <v-col cols="11" class="d-flex justify-center align-top mx-2">
                <!-- add rule rowNameDoenstExist -->
                <v-text-field-filled
                    v-model="nameIB"
                    :rules="[rules.notEmpty]"
                    :readonly="readOnly"
                    label="Nom"
                    density="compact"
                />
                <v-checkbox
                    v-model="displaySignIB"
                    label="Inverser le signe"
                    :true-value="1"
                    :false-value="-1"
                    class="ma-0"
                    hint="Si coché, les charges seront de signe positif (et les produits de signe négatif)"
                    persistent-hint
                    density="compact"
                    :readonly="readOnly"
                />
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col cols="12" class="title pl-3 pt-2"> Conditions </v-col>
            <v-col cols="12" class="pl-3 pt-2">
                <v-row
                    no-gutters
                    v-for="(cont, index) of rulesIB"
                    :key="index + '_' + refreshKey"
                    class="d-flex justify-center"
                >
                    <v-col cols="1" class="d-flex align-center justify-center">
                        {{ index !== 0 ? "ET" : "SI" }}
                    </v-col>
                    <v-col cols="2" class="pb-2">
                        <v-select-filled
                            v-model="cont.operator"
                            :items="operators"
                            :rules="[rules.notEmpty]"
                            :readonly="readOnly"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="5" class="pb-2 pl-2" v-if="cont.operator === 'equalTo'">
                        <v-autocomplete-filled
                            v-model="cont.numbers"
                            :items="scopedAccountableAccounts"
                            :item-title="(item) => displayVChip(item.name || item)"
                            :item-value="(item) => item.name"
                            :rules="[rules.notEmptyAutocomplete]"
                            :readonly="readOnly"
                            label="l'un de ces comptes comptables"
                            chips
                            multiple
                            closable-chips
                        />
                    </v-col>
                    <v-col cols="5" class="pb-2 pl-2" v-else-if="cont.operator === 'startsWith'">
                        <v-combobox-filled
                            v-model="cont.numbers"
                            label="un de ces comptes racines"
                            :item-title="(item) => displayVChip(item)"
                            multiple
                            density="compact"
                            chips
                            :closable-chips="!readOnly"
                            item-disabled
                            :readonly="readOnly"
                            :rules="[rules.notEmptyAutocomplete, rules.onlyNumbers]"
                            append-inner-icon="$fa-ban"
                            @click:appendInner="displayNotStartsWith(cont)"
                        />
                        <div>
                            <!-- v-if="cont.displayNotStartsWith" -->
                            <v-combobox-filled
                                v-model="cont.notStartsWith"
                                label="Et ne démarre pas par"
                                multiple
                                density="compact"
                                chips
                                closable-chips
                                :items="scopedAccountableAccounts"
                                :item-value="(item) => item.name"
                                :item-title="(item) => displayVChip(item.name || item)"
                                :return-object="false"
                                :rules="[rules.onlyNumbers]"
                                :readonly="readOnly"
                                v-show="
                                    (cont.notStartsWith && cont.notStartsWith.length) ||
                                    cont.displayNotStartsWith
                                "
                            />
                        </div>
                    </v-col>
                    <v-col
                        cols="3"
                        class="pb-1"
                        v-if="cont.operator === 'startsWith' || cont.operator === 'equalTo'"
                    >
                        <v-select-filled
                            v-model="cont.debitOrCredit"
                            :items="operatorsDebitOrCredit"
                            :readonly="readOnly"
                            class="ml-2"
                            density="compact"
                        />
                    </v-col>
                    <v-col cols="8" v-else></v-col>
                    <v-col col="1" class="d-flex align-top justify-center">
                        <v-btn-trash
                            v-if="rulesIB.length > 1"
                            :readonly="readOnly"
                            size="x-small"
                            @click="removeCondition(index)"
                        />
                        <v-btn-add-icon
                            v-if="index + 1 === rulesIB.length"
                            :readonly="readOnly"
                            size="x-small"
                            @click="addCondition()"
                        ></v-btn-add-icon>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
    </v-form>
</template>

<script lang="ts">
import UPSERT_COMPUTED_INDICATOR from "../../graphql/UpsertComputedIndicator.gql";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";

interface ComputedIndicatorIndicator {
    // saved like this in BDD
    name: String;
    columns: any; // { netAmounts: Array<{startsWith: Array<String>, notStartsWith: Array<String>}>}
    displaySign?: 1 | -1;
    key?: String;
}

interface ComputedIndicatorRule {
    // used like this in this component (conversion en entrée & en sortie)
    operator: "startsWith" | "equalTo";
    numbers: Array<String>;
    debitOrCredit: "both" | "debitOnly" | "creditOnly";
    notStartsWith: Array<String>;
}

@Component
export class ComputedIndicatorCreator extends Vue {
    @Prop() incomingData: any; // ComputedIndicator   // can be an ID or directly an ComputedIndicator
    @Prop() companyId: String;
    @Prop({ default: false }) readOnly: boolean;
    @Prop({ default: false }) dontSaveInDB: boolean; // return the new ComputedIndicator instead of saving it in DataBase
    @Prop({ default: null }) limitationRangeDico: Array<String>;
    @Prop({ default: null }) format; // not used actually  => to manage different formats than 1 column => netAmount
    @Prop({ default: null }) actions: any;
    @Prop({ default: "" }) scopeId: string;
    @Prop({ default: false }) watchScope: any;

    defaultIB: ComputedIndicatorIndicator = {
        // Question_Maxime comment bien gérer typage des Json ?   + mieux de directement travailler sur les données de la BDD ou de passer par le traitement en entrée et en sortie comme ici ?
        name: "",
        columns: {
            netAmounts: [
                {
                    startsWith: [],
                },
            ],
        },
        displaySign: -1,
    };

    nameIB: string = "";
    displaySignIB: 1 | -1 = 1;
    isValid = false;
    loading = false;
    rulesIB: Array<ComputedIndicatorRule> = [
        { operator: "startsWith", numbers: [], debitOrCredit: "both", notStartsWith: [] },
    ];

    refreshKey = 0; // to delete ?
    scopedAccountableAccounts = []; // to delete ?

    // _companyId: String = null

    operators: Array<{ title: String; value: ComputedIndicatorRule["operator"] }> = [
        { title: "Egal à", value: "equalTo" },
        { title: "Démarre par", value: "startsWith" },
    ];

    operatorsDebitOrCredit: Array<{
        title: String;
        value: ComputedIndicatorRule["debitOrCredit"];
    }> = [
        { title: "Débit et crédit", value: "both" },
        { title: "Debit uniquement", value: "debitOnly" },
        { title: "Credit uniquement", value: "creditOnly" },
    ];

    beforeMount() {
        if (this.actions) {
            this.actions.action.title = this.incomingData ? "Modifier" : "Créer";
            this.actions.action.function = this.saveOrReturnNewIB;
            this.actions.action.display = () => {
                return !this.readOnly;
            };
            this.actions.action.disabled = () => {
                return !this.isValid || (this.watchScope && !!!this.scopeId);
            };
            this.actions.action.loading = () => {
                return this.loading;
            };
        }

        if (this.incomingData?.definition) {
            const incomingIndicator: any = JSON.parse(JSON.stringify(this.incomingData.definition));
            this.nameIB = incomingIndicator.name || incomingIndicator.values[0].displayName;
            this.displaySignIB =
                incomingIndicator.displaySign || incomingIndicator.values[0].displaySign || -1;
            if (incomingIndicator.values?.[0]?.balanceRules?.[0]) {
                this.rulesIB = this.convertNetAmountsIntoNewRulesRow(
                    incomingIndicator.values[0].balanceRules,
                );
            }
            // this.rulesIB = incomingIndicator.values?.[0]?.balanceRules?.[0] || [];
        }
    }

    get rules() {
        return {
            notEmpty: (value) => {
                return !!value || "A remplir";
            },
            notEmptyAutocomplete: (value) => {
                if (value instanceof Array && value.length == 0) {
                    return "A remplir";
                }
                return !!value || "A remplir";
            },
            onlyNumbers: (value) => {
                for (const v of value) {
                    if (/^[0-9]{6}_.+/.test(v)) {
                        return true;
                    } // for subAccountableAccountOf
                    if (v.startsWith("0") || !/^[0-9]+$/.test(v)) {
                        return "Seuls des comptes racines sont acceptés";
                    }
                }
                return true;
            },
            // rowNameDoesntExist: value => {
            //     const index = this.OA_Tables[0].rows.findIndex(e => e.name === value || e.title === value)
            //     if(index !== -1 && index !== this.index) {
            //         return 'Ce nom de ligne existe déjà'
            //     }
            //     return true
            // }
        };
    }

    get _companyId() {
        return this.companyId || this.$store.state.user?.company?.id;
    }

    removeCondition(index) {
        this.rulesIB.splice(index, 1);
    }

    addCondition() {
        this.rulesIB.push({
            operator: "startsWith",
            numbers: [],
            debitOrCredit: "both",
            notStartsWith: [],
        });
    }

    subAccountableAccounts = [];

    displayNotStartsWith(cont) {
        cont.displayNotStartsWith = !cont.displayNotStartsWith;
        this.refreshKey++;
    }

    displayVChip(item) {
        if (/^[0-9]{6}_[a-zA-Z0-9]{32}/.test(item)) {
            const result = this.subAccountableAccounts[item];
            if (!result || !result.displayName) {
                return "";
            }
            return result.displayName + "_" + result.description;
        }
        return item;
    }

    async saveOrReturnNewIB() {
        this.loading = true;
        const id = this.incomingData?.id || "";
        const companyId = this._companyId;
        const resultDefinition = {
            values: [
                {
                    type: "BALANCE",
                    operationName: "v1",
                    displayName: this.nameIB,
                    displaySign: this.displaySignIB,
                    balanceRules: this.convertNewRulesRowIntoNetAmounts(this.rulesIB),
                },
            ],
            operator: "v1",
        };
        if (this.dontSaveInDB) {
            this.loading = false;
            return { resultDefinition, id, companyId };
        } else {
            const result = await this.$apollo.mutate({
                mutation: UPSERT_COMPUTED_INDICATOR,
                variables: {
                    id,
                    companyId,
                    definition: resultDefinition,

                    // companyScopeId: this.scopeId
                },
            });
            this.$emit("computedIndicatorSavedInBDD", {
                id: result.data.upsertComputedIndicator.id,
            });
            this.loading = false;
        }
    }

    pushAdaptedCondition(adaptedRulesRow, condition) {
        if (condition.operator === "equalTo") {
            if (condition.debitOrCredit === "creditOnly") {
                adaptedRulesRow.push({ startsWith: condition.numbers, creditOnly: true });
            } else if (condition.debitOrCredit === "debitOnly") {
                adaptedRulesRow.push({ startsWith: condition.numbers, debitOnly: true });
            } else {
                adaptedRulesRow.push({ startsWith: condition.numbers });
            }
        } else if (condition.operator === "startsWith") {
            if (condition.notStartsWith && condition.notStartsWith.length) {
                if (condition.debitOrCredit === "creditOnly") {
                    adaptedRulesRow.push({
                        startsWith: condition.numbers,
                        notStartsWith: condition.notStartsWith,
                        creditOnly: true,
                    });
                } else if (condition.debitOrCredit === "debitOnly") {
                    adaptedRulesRow.push({
                        startsWith: condition.numbers,
                        notStartsWith: condition.notStartsWith,
                        debitOnly: true,
                    });
                } else {
                    adaptedRulesRow.push({
                        startsWith: condition.numbers,
                        notStartsWith: condition.notStartsWith,
                    });
                }
            } else {
                if (condition.debitOrCredit === "creditOnly") {
                    adaptedRulesRow.push({ startsWith: condition.numbers, creditOnly: true });
                } else if (condition.debitOrCredit === "debitOnly") {
                    adaptedRulesRow.push({ startsWith: condition.numbers, debitOnly: true });
                } else {
                    adaptedRulesRow.push({ startsWith: condition.numbers });
                }
            }
        }
    }

    convertNewRulesRowIntoNetAmounts(newRulesRow) {
        const result = [];
        for (const rule of newRulesRow) {
            this.pushAdaptedCondition(result, rule);
        }
        return result;
    }

    convertNetAmountsIntoNewRulesRow(conditions) {
        const adaptedNetAmounts = [];
        for (const condition of conditions)
            if (condition.notStartsWith && condition.notStartsWith.length) {
                if (condition.creditOnly) {
                    adaptedNetAmounts.push({
                        operator: "startsWith",
                        numbers: condition.startsWith,
                        debitOrCredit: "creditOnly",
                        notStartsWith: condition.notStartsWith,
                    });
                } else if (condition.debitOnly) {
                    adaptedNetAmounts.push({
                        operator: "startsWith",
                        numbers: condition.startsWith,
                        debitOrCredit: "debitOnly",
                        notStartsWith: condition.notStartsWith,
                    });
                } else {
                    adaptedNetAmounts.push({
                        operator: "startsWith",
                        numbers: condition.startsWith,
                        debitOrCredit: "both",
                        notStartsWith: condition.notStartsWith,
                    });
                }
            } else {
                if (condition.creditOnly) {
                    adaptedNetAmounts.push({
                        operator: "startsWith",
                        numbers: condition.startsWith,
                        debitOrCredit: "creditOnly",
                        notStartsWith: [],
                    });
                } else if (condition.debitOnly) {
                    adaptedNetAmounts.push({
                        operator: "startsWith",
                        numbers: condition.startsWith,
                        debitOrCredit: "debitOnly",
                        notStartsWith: [],
                    });
                } else {
                    adaptedNetAmounts.push({
                        operator: "startsWith",
                        numbers: condition.startsWith,
                        debitOrCredit: "both",
                        notStartsWith: [],
                    });
                }
            }
        return adaptedNetAmounts;
    }
}

export default toNative(ComputedIndicatorCreator);
</script>
